import { apiSlice } from "../api/apiSlice";
import { Event } from "../../types";
import { mallsApiSlice } from "../malls/mallsApiSlice";
import { CACHE_TIME } from "../api/apiHelpers";

/**
 * Events API Slice
 * RTK Query endpoints for event management
 */

interface GetEventsParams {
  mallId?: string;
  category?: string;
  startDate?: string;
  endDate?: string;
  status?: string;
}

interface GetEventsResponse {
  success: boolean;
  events: Event[];
  total: number;
  hasMore?: boolean;
}

interface GetEventsByMallResponse {
  success: boolean;
  events: Event[];
  mallId: string;
  mallName: string;
  total: number;
  pagination?: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

interface EventsData {
  events: Event[];
  pagination?: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

interface GetEventByIdResponse {
  success: boolean;
  event: Event;
}

export const eventsApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Get all featured events or events for a specific mall
     * - mallId="all" → /api/events/all (featured events)
     * - mallId=specific → /api/events/mall/:mallId (mall-specific events)
     * - mallId=undefined → /api/events/all (featured events as fallback)
     */
    getEvents: builder.query<Event[], GetEventsParams | void>({
      query: (params) => {
        // Handle void/undefined params
        if (!params) {
          return {
            url: "/api/events/all",
            params: {},
          };
        }

        const { mallId, category, startDate, endDate, status, ...rest } =
          params;

        // Determine endpoint based on mallId
        const endpoint =
          !mallId || mallId === "all"
            ? "/api/events/all"
            : `/api/events/mall/${mallId}`;

        return {
          url: endpoint,
          params: {
            category,
            startDate,
            endDate,
            status,
            ...rest,
          },
        };
      },
      transformResponse: (response: GetEventsResponse) => response.events,
      providesTags: (result, error, params) => {
        const mallId = params?.mallId || "all";
        return result
          ? [
              ...result.map(({ id }) => ({ type: "Events" as const, id })),
              { type: "Events", id: "LIST" },
              {
                type: "Events",
                id: mallId === "all" ? "ALL" : `MALL-${mallId}`,
              },
            ]
          : [{ type: "Events", id: "LIST" }];
      },
    }),

    // Get events by mall ID
    getEventsByMall: builder.query<
      EventsData,
      { mallId: string; page?: number; limit?: number; searchQuery?: string }
    >({
      query: ({ mallId, page = 1, limit = 12, searchQuery }) => ({
        url: `/api/events/mall/${mallId}`,
        params: { page, limit, search: searchQuery },
      }),
      transformResponse: (response: GetEventsByMallResponse) => ({
        events: response.events,
        pagination: response.pagination,
      }),
      providesTags: (result, error, { mallId }) => [
        { type: "Events", id: `MALL-${mallId}` },
      ],
      keepUnusedDataFor: CACHE_TIME.LONG, // Events don't change frequently - 10 min cache
    }),

    // Get single event by ID
    getEventById: builder.query<Event, string>({
      query: (id) => `/api/events/${id}`,
      transformResponse: (response: GetEventByIdResponse) => response.event,
      async onCacheEntryAdded(
        eventId,
        { cacheDataLoaded, cacheEntryRemoved, dispatch, getState }
      ) {
        try {
          // Wait for the data to be loaded
          const { data: event } = await cacheDataLoaded;

          // Update all getEventsByMall cache entries with the updated counts
          // Backend auto-increments view count, so we sync it to events lists
          const state: any = getState();
          const cacheEntries = state.api?.queries || {};

          Object.keys(cacheEntries).forEach((cacheKey) => {
            if (cacheKey.startsWith("getEventsByMall")) {
              const entry = cacheEntries[cacheKey];
              const mallId = entry?.originalArgs?.mallId;

              if (mallId && event.mallId === mallId) {
                try {
                  dispatch(
                    eventsApiSlice.util.updateQueryData(
                      "getEventsByMall",
                      entry.originalArgs,
                      (draft) => {
                        const cachedEvent = draft.events.find(
                          (e: Event) => e.id === eventId
                        );
                        if (cachedEvent) {
                          cachedEvent.viewCount = event.viewCount;
                          cachedEvent.shareCount = event.shareCount;
                        }
                      }
                    )
                  );
                } catch (e) {
                  // Ignore individual cache update errors
                }
              }
            }
          });

          await cacheEntryRemoved;
        } catch {
          // Ignore errors during cache lifecycle
        }
      },
      providesTags: (result, error, id) => [{ type: "Events", id }],
    }),

    // Mark user interest in an event
    markEventInterest: builder.mutation<
      { success: boolean; interestedCount: number },
      { eventId: string; interested: boolean }
    >({
      query: ({ eventId, interested }) => ({
        url: `/api/events/${eventId}/interest`,
        method: "POST",
        body: { interested },
      }),
      invalidatesTags: (result, error, { eventId }) => [
        { type: "Events", id: eventId },
      ],
    }),

    // Increment event view count
    incrementEventView: builder.mutation<
      void,
      { eventId: string; mallId: string }
    >({
      query: ({ eventId }) => ({
        url: `/api/events/${eventId}/view`,
        method: "POST",
      }),
      async onQueryStarted({ eventId, mallId }, { dispatch, queryFulfilled }) {
        // Optimistically update cache before API response
        const patchResult = dispatch(
          mallsApiSlice.util.updateQueryData("getMallHub", mallId, (draft) => {
            const mallHubDraft = draft as any;
            const event = mallHubDraft.events?.find(
              (e: Event) => e.id === eventId
            );
            if (event) {
              event.viewCount = (event.viewCount || 0) + 1;
            }
          })
        );

        try {
          await queryFulfilled;
        } catch {
          // Rollback optimistic update on error
          patchResult.undo();
        }
      },
      invalidatesTags: (result, error, { eventId }) => [
        { type: "Events", id: eventId },
      ],
    }),

    // Increment event share count
    incrementEventShare: builder.mutation<void, string>({
      query: (eventId) => ({
        url: `/api/events/${eventId}/share`,
        method: "POST",
      }),
      // Only invalidate list cache to update cards, not individual item to avoid refetch
      invalidatesTags: () => [{ type: "Events", id: "LIST" }],
    }),
  }),
});

export const {
  useGetEventsQuery,
  useGetEventsByMallQuery,
  useGetEventByIdQuery,
  useMarkEventInterestMutation,
  useIncrementEventViewMutation,
  useIncrementEventShareMutation,
} = eventsApiSlice;
